import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import bedrockService from '../services/bedrockService';
import agentService from '../services/agentService';
import mcpService from '../services/mcpService';
import promptsService from '../services/promptsService';
import toolApprovalsService from '../services/toolApprovalsService';
import ToolUseNode from './ToolUseNode';
import ToolApprovals from './ToolApprovals';
import './BedrockChat.css';

const AGENT_PARENT_API_URL = process.env.REACT_APP_AGENT_PARENT_API_URL || 'https://your-agent-parent-api-url.execute-api.us-east-1.amazonaws.com/prod';

function BedrockChat({ authResult, onOpenSettings, onOpenSecrets, onOpenAgents, onOpenExternalMcpServers, onSignOut }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [userRoleArn, setUserRoleArn] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [useStreaming, setUseStreaming] = useState(true);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('global.amazon.nova-2-lite-v1:0');
  const [loadingModels, setLoadingModels] = useState(false);
  const [availableGateways, setAvailableGateways] = useState([]);
  const [selectedGateways, setSelectedGateways] = useState([]);
  const [loadingGateways, setLoadingGateways] = useState(false);
  const [mcpTools, setMcpTools] = useState([]);
  const [connectingToMcp, setConnectingToMcp] = useState(false);
  const [showToolsPanel, setShowToolsPanel] = useState(false);
  const [selectedToolForTest, setSelectedToolForTest] = useState(null);
  const [toolTestParams, setToolTestParams] = useState({});
  const [toolTestResult, setToolTestResult] = useState(null);
  const [testingTool, setTestingTool] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [showPromptsPanel, setShowPromptsPanel] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptText, setNewPromptText] = useState('');
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editPromptName, setEditPromptName] = useState('');
  const [editPromptText, setEditPromptText] = useState('');
  const [showToolApprovals, setShowToolApprovals] = useState(false);
  const [toolApprovals, setToolApprovals] = useState([]);
  const [pendingToolApproval, setPendingToolApproval] = useState(null);
  const [approvalResolve, setApprovalResolve] = useState(null);
  const messagesEndRef = useRef(null);
  const shouldStopRef = useRef(false);
  const [usageStats, setUsageStats] = useState({
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    cacheReadTokens: 0,
    cacheWriteTokens: 0,
    stopReason: null,
    mode: 'chat', // 'chat' or 'tools'
    toolCallsCount: 0,
  });
  const [systemPrompt, setSystemPrompt] = useState('');

  useEffect(() => {
    loadUserArn();
    loadAvailableModels();
    loadAvailableGateways();
    loadSystemPrompt();
    loadSavedPrompts();
    loadAgentTools();
    loadToolApprovals();

    // Listen for agent updates
    const handleAgentsUpdated = () => {
      console.log('Agents updated, reloading agent tools...');
      loadAgentTools();
    };

    // Listen for tool approvals updates
    const handleToolApprovalsUpdated = () => {
      console.log('Tool approvals updated, reloading...');
      loadToolApprovals();
    };
    
    window.addEventListener('agentsUpdated', handleAgentsUpdated);
    window.addEventListener('toolApprovalsUpdated', handleToolApprovalsUpdated);
    
    // Cleanup
    return () => {
      window.removeEventListener('agentsUpdated', handleAgentsUpdated);
      window.removeEventListener('toolApprovalsUpdated', handleToolApprovalsUpdated);
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Reload models when role ARN changes (in case permissions differ)
    if (userRoleArn) {
      loadAvailableModels();
    }
  }, [userRoleArn]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadUserArn = async () => {
    try {
      const userData = await agentService.getUserArn(AGENT_PARENT_API_URL);
      if (userData && userData.roleArn) {
        setUserRoleArn(userData.roleArn);
      }
    } catch (err) {
      console.error('Error loading user ARN:', err);
    }
  };

  const loadAvailableModels = async () => {
    setLoadingModels(true);
    try {
      // Configure Bedrock service with role ARN
      if (!userRoleArn) {
        // Don't attempt to load models if role isn't configured
        setLoadingModels(false);
        return;
      }
      
      bedrockService.setUserRole(userRoleArn);

      const models = await bedrockService.listModels();
      setAvailableModels(models);
    } catch (err) {
      console.error('Error loading available models:', err);
      // Don't show error for first load, just use default model
      if (availableModels.length === 0) {
        console.log('Using default model');
      } else {
        setError('Failed to refresh models list.');
      }
    } finally {
      setLoadingModels(false);
    }
  };

  const loadAvailableGateways = async () => {
    setLoadingGateways(true);
    try {
      const gateways = await agentService.listGateways(AGENT_PARENT_API_URL);
      setAvailableGateways(gateways);
    } catch (err) {
      console.error('Error loading available gateways:', err);
    } finally {
      setLoadingGateways(false);
    }
  };

  const loadSystemPrompt = () => {
    try {
      const saved = localStorage.getItem('systemPrompt');
      if (saved) {
        setSystemPrompt(saved);
      }
    } catch (err) {
      console.error('Error loading system prompt:', err);
    }
  };

  const saveSystemPrompt = (prompt) => {
    try {
      localStorage.setItem('systemPrompt', prompt);
      setSystemPrompt(prompt);
    } catch (err) {
      console.error('Error saving system prompt:', err);
    }
  };

  const loadSavedPrompts = async () => {
    try {
      const prompts = await promptsService.getPrompts();
      setSavedPrompts(prompts);
    } catch (err) {
      console.error('Error loading saved prompts:', err);
    }
  };

  const loadToolApprovals = async () => {
    try {
      const approvals = await toolApprovalsService.getToolApprovals();
      setToolApprovals(approvals);
    } catch (err) {
      console.error('Error loading tool approvals:', err);
    }
  };

  const loadAgentTools = () => {
    try {
      const agentsJson = localStorage.getItem('agents');
      if (agentsJson) {
        const agents = JSON.parse(agentsJson);
        if (agents.length > 0) {
          // Add the invoke_sub_agent tool to tool specs
          const agentTool = {
            toolSpec: {
              name: 'invoke_sub_agent',
              description: 'Invoke a sub-agent to handle a specific task. Use this to delegate work to specialized agents in your hierarchy.',
              inputSchema: {
                json: {
                  type: 'object',
                  properties: {
                    agent_id: {
                      type: 'string',
                      description: 'The ID of the sub-agent to invoke',
                    },
                    message: {
                      type: 'string',
                      description: 'The message or task to send to the sub-agent',
                    },
                  },
                  required: ['agent_id', 'message'],
                },
              },
            },
          };
          
          // Update tool specs to include agent tool
          const currentToolSpecs = bedrockService.getToolSpecs() || [];
          const hasAgentTool = currentToolSpecs.some(t => t.toolSpec?.name === 'invoke_sub_agent');
          if (!hasAgentTool) {
            bedrockService.setToolSpecs([...currentToolSpecs, agentTool]);
          }
        }
      }
    } catch (err) {
      console.error('Error loading agent tools:', err);
    }
  };

  const invokeSubAgent = async (agentId, message) => {
    try {
      const agentsJson = localStorage.getItem('agents');
      if (!agentsJson) {
        throw new Error('No agents configured');
      }
      
      const agents = JSON.parse(agentsJson);
      const agent = agents.find(a => a.id === agentId);
      
      if (!agent) {
        throw new Error(`Agent with ID ${agentId} not found`);
      }
      
      // Use the agent's system prompt to invoke Bedrock
      const agentSystemPrompt = agent.systemPrompt || `You are ${agent.name}. ${agent.description || ''}`;
      
      // Create a sub-conversation with the agent's context
      const result = await bedrockService.sendMessageWithTools(
        message,
        selectedModel,
        [], // Start with empty history for sub-agent
        agentSystemPrompt
      );
      
      return {
        agent_name: agent.name,
        agent_id: agentId,
        response: result.response || 'No response from sub-agent',
        usage: result.usage,
      };
    } catch (err) {
      console.error('Error invoking sub-agent:', err);
      throw err;
    }
  };

  const handleSavePrompt = async () => {
    if (!newPromptName.trim() || !newPromptText.trim()) {
      setError('Please enter both a prompt name and prompt text');
      return;
    }

    setSavingPrompt(true);
    try {
      await promptsService.putPrompt(newPromptName, newPromptText);
      setNewPromptName('');
      setNewPromptText('');
      await loadSavedPrompts();
      setError('');
    } catch (err) {
      setError('Failed to save prompt: ' + err.message);
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleStartEdit = (prompt) => {
    setEditingPrompt(prompt.promptName);
    setEditPromptName(prompt.promptName);
    setEditPromptText(prompt.promptText);
  };

  const handleCancelEdit = () => {
    setEditingPrompt(null);
    setEditPromptName('');
    setEditPromptText('');
  };

  const handleSaveEdit = async () => {
    if (!editPromptText.trim()) {
      setError('Prompt text cannot be empty');
      return;
    }

    setSavingPrompt(true);
    try {
      // If name changed, delete old and create new
      if (editPromptName !== editingPrompt) {
        await promptsService.deletePrompt(editingPrompt);
      }
      await promptsService.putPrompt(editPromptName, editPromptText);
      setEditingPrompt(null);
      setEditPromptName('');
      setEditPromptText('');
      await loadSavedPrompts();
      setError('');
    } catch (err) {
      setError('Failed to update prompt: ' + err.message);
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleLoadPrompt = (prompt) => {
    setInputMessage(prompt.promptText);
    setShowPromptsPanel(false);
  };

  const handleDeletePrompt = async (promptName) => {
    if (!window.confirm(`Are you sure you want to delete the prompt "${promptName}"?`)) {
      return;
    }

    try {
      await promptsService.deletePrompt(promptName);
      await loadSavedPrompts();
    } catch (err) {
      setError('Failed to delete prompt: ' + err.message);
    }
  };

  /**
   * Get the system prompt with current date injected
   * @returns {string|null} System prompt with date, or null if no prompt
   */
  const getSystemPromptWithDate = () => {
    const now = new Date();
    const dateString = now.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const timeString = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const dateInfo = `Current date and time: ${dateString} at ${timeString}`;
    
    if (systemPrompt && systemPrompt.trim()) {
      return `${dateInfo}\n\n${systemPrompt}`;
    }
    
    return dateInfo;
  };

  // Get the approval status for a tool
  const getToolApprovalStatus = (toolName) => {
    const approval = toolApprovals.find(a => a.toolName === toolName);
    return approval ? approval.approvalStatus : 'require_approval'; // Default to require approval
  };

  // Request user approval for a tool
  const requestToolApproval = (toolName, toolInput) => {
    return new Promise((resolve) => {
      setPendingToolApproval({ toolName, toolInput });
      setApprovalResolve(() => resolve);
    });
  };

  // Handle user approval response
  const handleApprovalResponse = (approved, rememberChoice = false) => {
    if (approvalResolve) {
      approvalResolve({ approved, rememberChoice });
    }
    setPendingToolApproval(null);
    setApprovalResolve(null);
  };

  // Helper function to execute tools and continue conversation
  const executeToolsAndContinue = async (result, currentHistory) => {
    let history = currentHistory;
    let currentResult = result;
    
    // Agentic loop: keep executing tools until we get a final response
    while (currentResult.stopReason === 'tool_use' && currentResult.toolUse && currentResult.toolUse.length > 0) {
      // Check if user requested to stop
      if (shouldStopRef.current) {
        console.log('Execution stopped by user');
        return { ...currentResult, conversationHistory: history, stopped: true };
      }
      
      console.log('Executing tools:', currentResult.toolUse);
      
      // Add all tool use nodes with pending/executing status first
      const toolBlocks = currentResult.toolUse.map(toolBlock => {
        const { toolUseId, name, input } = toolBlock.toolUse;
        const approvalStatus = getToolApprovalStatus(name);
        return {
          type: 'tool',
          toolUse: {
            id: toolUseId,
            name,
            input,
            status: approvalStatus === 'auto_approve' ? 'executing' : 'pending_approval',
            approvalStatus
          }
        };
      });
      
      // Update UI with all executing tools at once
      setMessages(prev => {
        const newMessages = prev.slice(0, -1);
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          const contentBlocks = [...(lastMessage.contentBlocks || []), ...toolBlocks];
          const toolUses = toolBlocks.map(tb => tb.toolUse);
          return [
            ...newMessages,
            {
              ...lastMessage,
              contentBlocks,
              toolUses: [...(lastMessage.toolUses || []), ...toolUses]
            }
          ];
        }
        return prev;
      });
      
      // Execute all tools (with approval flow)
      const toolPromises = currentResult.toolUse.map(async (toolBlock) => {
        const { toolUseId, name, input } = toolBlock.toolUse;
        const approvalStatus = getToolApprovalStatus(name);
        
        try {
          // Check approval status
          if (approvalStatus === 'always_deny') {
            // Update tool use node with denied status
            setMessages(prev => {
              const newMessages = prev.slice(0, -1);
              const lastMessage = prev[prev.length - 1];
              if (lastMessage && lastMessage.role === 'assistant') {
                const contentBlocks = (lastMessage.contentBlocks || []).map(block =>
                  block.type === 'tool' && block.toolUse.id === toolUseId
                    ? { ...block, toolUse: { ...block.toolUse, status: 'denied', error: 'Tool is configured to always be denied' } }
                    : block
                );
                return [
                  ...newMessages,
                  { ...lastMessage, contentBlocks }
                ];
              }
              return prev;
            });
            
            return {
              toolResult: {
                toolUseId: toolUseId,
                content: [{ text: 'Error: Tool execution denied by user configuration' }],
                status: 'error',
              },
            };
          }
          
          // Check if approval is required
          if (approvalStatus === 'require_approval') {
            // Update tool use node to show pending approval
            setMessages(prev => {
              const newMessages = prev.slice(0, -1);
              const lastMessage = prev[prev.length - 1];
              if (lastMessage && lastMessage.role === 'assistant') {
                const contentBlocks = (lastMessage.contentBlocks || []).map(block =>
                  block.type === 'tool' && block.toolUse.id === toolUseId
                    ? { ...block, toolUse: { ...block.toolUse, status: 'pending_approval' } }
                    : block
                );
                return [
                  ...newMessages,
                  { ...lastMessage, contentBlocks }
                ];
              }
              return prev;
            });
            
            // Request user approval
            const { approved, rememberChoice } = await requestToolApproval(name, input);
            
            if (!approved) {
              // Update tool use node with denied status
              setMessages(prev => {
                const newMessages = prev.slice(0, -1);
                const lastMessage = prev[prev.length - 1];
                if (lastMessage && lastMessage.role === 'assistant') {
                  const contentBlocks = (lastMessage.contentBlocks || []).map(block =>
                    block.type === 'tool' && block.toolUse.id === toolUseId
                      ? { ...block, toolUse: { ...block.toolUse, status: 'denied', error: 'Tool execution denied by user' } }
                      : block
                  );
                  return [
                    ...newMessages,
                    { ...lastMessage, contentBlocks }
                  ];
                }
                return prev;
              });
              
              // Save the deny choice if user wants to remember
              if (rememberChoice) {
                try {
                  await toolApprovalsService.putToolApproval(name, 'always_deny');
                  await loadToolApprovals();
                } catch (err) {
                  console.error('Error saving tool approval:', err);
                }
              }
              
              return {
                toolResult: {
                  toolUseId: toolUseId,
                  content: [{ text: 'Error: Tool execution denied by user' }],
                  status: 'error',
                },
              };
            }
            
            // Save the approve choice if user wants to remember
            if (rememberChoice) {
              try {
                await toolApprovalsService.putToolApproval(name, 'auto_approve');
                await loadToolApprovals();
              } catch (err) {
                console.error('Error saving tool approval:', err);
              }
            }
          }
          
          // Update tool use node to show executing
          setMessages(prev => {
            const newMessages = prev.slice(0, -1);
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              const contentBlocks = (lastMessage.contentBlocks || []).map(block =>
                block.type === 'tool' && block.toolUse.id === toolUseId
                  ? { ...block, toolUse: { ...block.toolUse, status: 'executing' } }
                  : block
              );
              return [
                ...newMessages,
                { ...lastMessage, contentBlocks }
              ];
            }
            return prev;
          });
          
          // Check if this is the invoke_sub_agent tool
          let toolResult;
          if (name === 'invoke_sub_agent') {
            // Handle sub-agent invocation locally
            console.log('Invoking sub-agent:', input);
            toolResult = await invokeSubAgent(input.agent_id, input.message);
            console.log('Sub-agent result:', toolResult);
          } else {
            // Call the tool via MCP
            toolResult = await mcpService.callTool(name, input);
            console.log('Tool result:', toolResult);
          }
          
          // Format tool result for Bedrock
          let resultContent = '';
          if (name === 'invoke_sub_agent') {
            // Format sub-agent result
            resultContent = `Sub-agent ${toolResult.agent_name} responded:\n\n${toolResult.response}`;
          } else if (toolResult.content && Array.isArray(toolResult.content)) {
            resultContent = toolResult.content.map(c => c.text || JSON.stringify(c)).join('\n');
          } else {
            resultContent = JSON.stringify(toolResult);
          }
          
          // Update tool use node with completed status and result
          setMessages(prev => {
            const newMessages = prev.slice(0, -1);
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              const contentBlocks = (lastMessage.contentBlocks || []).map(block =>
                block.type === 'tool' && block.toolUse.id === toolUseId
                  ? { ...block, toolUse: { ...block.toolUse, status: 'completed', result: resultContent } }
                  : block
              );
              const toolUses = (lastMessage.toolUses || []).map(tu => 
                tu.id === toolUseId 
                  ? { ...tu, status: 'completed', result: resultContent }
                  : tu
              );
              return [
                ...newMessages,
                {
                  ...lastMessage,
                  contentBlocks,
                  toolUses
                }
              ];
            }
            return prev;
          });
          
          return {
            toolResult: {
              toolUseId: toolUseId,
              content: [{ text: resultContent }],
            },
          };
        } catch (toolError) {
          console.error('Tool execution error:', toolError);
          
          // Update tool use node with error status
          setMessages(prev => {
            const newMessages = prev.slice(0, -1);
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              const contentBlocks = (lastMessage.contentBlocks || []).map(block =>
                block.type === 'tool' && block.toolUse.id === toolUseId
                  ? { ...block, toolUse: { ...block.toolUse, status: 'error', error: toolError.message } }
                  : block
              );
              const toolUses = (lastMessage.toolUses || []).map(tu => 
                tu.id === toolUseId 
                  ? { ...tu, status: 'error', error: toolError.message }
                  : tu
              );
              return [
                ...newMessages,
                {
                  ...lastMessage,
                  contentBlocks,
                  toolUses
                }
              ];
            }
            return prev;
          });
          
          return {
            toolResult: {
              toolUseId: toolUseId,
              content: [{ text: `Error: ${toolError.message}` }],
              status: 'error',
            },
          };
        }
      });
      
      // Wait for all tools to complete in parallel
      const toolResults = await Promise.all(toolPromises);
      
      // Add tool results to conversation history and continue
      history = [
        ...currentResult.conversationHistory,
        {
          role: 'user',
          content: toolResults,
        },
      ];
      
      // Continue the conversation with tool results
      // Don't pass a message - we're just continuing with the updated history
      currentResult = await bedrockService.sendMessageWithTools(
        null, // No new user message, just continuing with tool results
        selectedModel,
        history,
        getSystemPromptWithDate()
      );
      
      // Update the assistant message with the new response
      if (currentResult.response) {
        setMessages(prev => {
          const newMessages = prev.slice(0, -1);
          const lastMessage = prev[prev.length - 1];
          if (lastMessage && lastMessage.role === 'assistant') {
            const contentBlocks = lastMessage.contentBlocks || [];
            return [
              ...newMessages,
              {
                ...lastMessage,
                content: lastMessage.content + '\n\n' + currentResult.response,
                contentBlocks: [
                  ...contentBlocks,
                  { type: 'text', content: currentResult.response }
                ]
              }
            ];
          }
          return prev;
        });
      }
    }
    
    return { ...currentResult, conversationHistory: history };
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages([...messages, userMessage]);
    setInputMessage('');
    setLoading(true);
    setError('');
    shouldStopRef.current = false; // Reset stop flag

    try {
      // Configure Bedrock service with role ARN
      if (!userRoleArn) {
        setError('User Role ARN must be configured. Please set it in Settings.');
        setLoading(false);
        return;
      }
      
      bedrockService.setUserRole(userRoleArn);

      // Always use Converse API (supports system prompts with or without tools)
      // Only difference is whether we enable tools or not
      const hasTools = selectedGateways.length > 0 && mcpTools.length > 0;

      if (true) {  // Always use Converse API now
        // Use Converse API with tool support
        const assistantMessage = {
          role: 'assistant',
          content: '',
          contentBlocks: [],
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, assistantMessage]);

        // Send initial message with system prompt (with date injected)
        let result = await bedrockService.sendMessageWithTools(
          inputMessage,
          selectedModel,
          conversationHistory,
          getSystemPromptWithDate()
        );

        // Update UI with initial response
        if (result.response) {
          setMessages(prev => {
            const newMessages = prev.slice(0, -1);
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              return [
                ...newMessages,
                {
                  ...lastMessage,
                  content: result.response,
                  contentBlocks: [
                    { type: 'text', content: result.response }
                  ]
                }
              ];
            }
            return prev;
          });
        }

        // If tool use is requested, execute tools and continue
        let toolCallsCount = 0;
        if (result.stopReason === 'tool_use' && result.toolUse) {
          toolCallsCount = result.toolUse.length;
          result = await executeToolsAndContinue(result, result.conversationHistory);
        }

        setConversationHistory(result.conversationHistory);
        
        // Update usage stats (including cache metrics)
        if (result.usage) {
          setUsageStats({
            inputTokens: result.usage.inputTokens || 0,
            outputTokens: result.usage.outputTokens || 0,
            totalTokens: result.usage.totalTokens || 0,
            cacheReadTokens: result.usage.cacheReadInputTokens || 0,
            cacheWriteTokens: result.usage.cacheCreationInputTokens || 0,
            stopReason: result.stopReason,
            mode: 'tools',
            toolCallsCount: toolCallsCount,
          });
        }
      } else {
        // Use standard InvokeModel API (no tools)
        if (useStreaming) {
          const assistantMessage = {
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
          };
          setMessages(prev => [...prev, assistantMessage]);

          const result = await bedrockService.sendMessageStream(
            inputMessage,
            selectedModel,
            conversationHistory,
            (chunk) => {
              setMessages(prev => {
                const newMessages = prev.slice(0, -1);
                const lastMessage = prev[prev.length - 1];
                if (lastMessage && lastMessage.role === 'assistant') {
                  return [
                    ...newMessages,
                    {
                      ...lastMessage,
                      content: lastMessage.content + chunk
                    }
                  ];
                }
                return prev;
              });
            }
          );

          setConversationHistory(result.conversationHistory);
        } else {
          const result = await bedrockService.sendMessage(
            inputMessage,
            selectedModel,
            conversationHistory
          );

          const assistantMessage = {
            role: 'assistant',
            content: result.response,
            timestamp: new Date().toISOString(),
          };

          setMessages(prev => [...prev, assistantMessage]);
          setConversationHistory(result.conversationHistory);
        }
      }
      
      // Update usage stats for non-tools mode (if available)
      setUsageStats(prev => ({
        ...prev,
        mode: 'chat',
        stopReason: null,
        toolCallsCount: 0,
      }));
    } catch (err) {
      if (shouldStopRef.current) {
        console.log('Message generation stopped by user');
      } else {
        setError('Failed to send message: ' + err.message);
        console.error('Error sending message:', err);
      }
    } finally {
      setLoading(false);
      shouldStopRef.current = false; // Reset stop flag
    }
  };

  const handleStopExecution = () => {
    shouldStopRef.current = true;
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearConversation = () => {
    console.log('Clearing conversation and aborting all active operations...');
    
    // Abort all active API requests before resetting state
    bedrockService.abortAll();
    mcpService.abortAll();
    
    // Signal any ongoing operations to stop
    shouldStopRef.current = true;
    setLoading(false);
    
    // Reset all conversation-related state
    setMessages([]);
    setConversationHistory([]);
    setError('');
    setUsageStats({
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0,
      cacheReadTokens: 0,
      cacheWriteTokens: 0,
      stopReason: null,
      mode: 'chat',
      toolCallsCount: 0,
    });
    
    // Clear any tool test state
    setSelectedToolForTest(null);
    setToolTestParams({});
    setToolTestResult(null);
    setTestingTool(false);
    
    // Reset stop flag after a brief delay to ensure cleanup completes
    setTimeout(() => {
      shouldStopRef.current = false;
    }, 100);
    
    console.log('Conversation cleared - fresh start');
  };

  const handleTestTool = async () => {
    if (!selectedToolForTest) return;
    
    setTestingTool(true);
    setToolTestResult(null);
    try {
      const result = await mcpService.callTool(selectedToolForTest.name, toolTestParams);
      setToolTestResult(result);
    } catch (err) {
      setToolTestResult({ error: err.message });
    } finally {
      setTestingTool(false);
    }
  };

  const handleToolTestParamChange = (paramName, value) => {
    setToolTestParams({
      ...toolTestParams,
      [paramName]: value,
    });
  };

  return (
    <div className="bedrock-chat-container">
      <div className="bedrock-chat-header">
        <h3>Larry Chat</h3>
        <div className="chat-controls">
          {mcpTools.length > 0 && (
            <button 
              onClick={() => setShowToolsPanel(!showToolsPanel)} 
              className={`tools-panel-button ${showToolsPanel ? 'active' : ''}`}
              title="View & Test MCP Tools"
            >
              🔧 Tools ({mcpTools.length})
            </button>
          )}
          <button 
            onClick={() => setShowConfig(!showConfig)} 
            className="config-button"
            title="Configuration"
          >
            ⚙️ Config
          </button>
          <button 
            onClick={onOpenSecrets} 
            className="config-button"
            title="Secrets"
          >
            🔐 Secrets
          </button>
          <button 
            onClick={() => setShowPromptsPanel(!showPromptsPanel)} 
            className={`config-button ${showPromptsPanel ? 'active' : ''}`}
            title="Saved Prompts"
          >
            📝 Prompts
          </button>
          <button 
            onClick={() => setShowToolApprovals(true)} 
            className="config-button"
            title="Tool Approvals"
          >
            🔐 Approvals
          </button>
          <button 
            onClick={onOpenAgents} 
            className="config-button"
            title="Agents"
          >
            🤖 Agents
          </button>
          <button 
            onClick={onOpenExternalMcpServers} 
            className="config-button"
            title="External MCP Servers"
          >
            🌐 MCP Servers
          </button>
          <button 
            onClick={onOpenSettings} 
            className="config-button"
            title="Settings"
          >
            👤 Settings
          </button>
          <button 
            onClick={clearConversation} 
            className="clear-button"
            title="Clear conversation"
          >
            🗑️
          </button>
          <button 
            onClick={onSignOut} 
            className="sign-out-button"
            title="Sign Out"
          >
            🚪 Sign Out
          </button>
        </div>
      </div>

      {showConfig && (
        <div className="chat-config">
          <div className="config-section">
            <label>Model:</label>
            <div className="model-selector">
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="config-input"
                disabled={loadingModels}
              >
                {loadingModels ? (
                  <option>Loading models...</option>
                ) : availableModels.length > 0 ? (
                  availableModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.id}
                    </option>
                  ))
                ) : (
                  <option value={selectedModel}>Larry AI (Default)</option>
                )}
              </select>
              <button
                onClick={loadAvailableModels}
                className="refresh-models-button"
                disabled={loadingModels}
                title="Refresh models"
              >
                🔄
              </button>
            </div>
          </div>
          <div className="config-section mcp-servers-section">
            <label>MCP Servers (Tool Access):</label>
            <button
              onClick={loadAvailableGateways}
              className="refresh-models-button"
              disabled={loadingGateways}
              title="Refresh MCP servers"
              style={{ marginBottom: '12px' }}
            >
              🔄 Refresh Servers
            </button>
            {loadingGateways ? (
              <div className="mcp-loading">⏳ Loading MCP servers...</div>
            ) : availableGateways.length === 0 ? (
              <div className="mcp-no-servers">⚠️ No MCP servers available</div>
            ) : (
              <div className="mcp-servers-list">
                {availableGateways.map((gateway) => {
                  const isSelected = selectedGateways.includes(gateway.gatewayArn);
                  const mcpUrl = `https://${gateway.gatewayId}.gateway.bedrock-agentcore.us-east-1.amazonaws.com/mcp`;
                  const isConnecting = connectingToMcp && isSelected && !mcpService.isConnected(mcpUrl);
                  
                  const handleToggle = async () => {
                    if (isSelected) {
                      // Disconnect from this gateway
                      setSelectedGateways(prev => prev.filter(arn => arn !== gateway.gatewayArn));
                      mcpService.disconnect(mcpUrl);
                      
                      // Update tools list from remaining connected gateways
                      const allTools = mcpService.getAllTools();
                      setMcpTools(allTools);
                      const toolSpecs = mcpService.getBedrockToolSpecs();
                      bedrockService.setToolSpecs(toolSpecs);
                    } else {
                      // Connect to this gateway
                      setSelectedGateways(prev => [...prev, gateway.gatewayArn]);
                      setConnectingToMcp(true);
                      try {
                        await mcpService.connect(mcpUrl);
                        const tools = await mcpService.listTools(mcpUrl);
                        
                        // Update tools list with all connected gateways
                        const allTools = mcpService.getAllTools();
                        setMcpTools(allTools);
                        const toolSpecs = mcpService.getBedrockToolSpecs();
                        bedrockService.setToolSpecs(toolSpecs);
                      } catch (err) {
                        console.error('Error connecting to MCP gateway:', err);
                        setError('Failed to connect to MCP gateway: ' + err.message);
                        setSelectedGateways(prev => prev.filter(arn => arn !== gateway.gatewayArn));
                      } finally {
                        setConnectingToMcp(false);
                      }
                    }
                  };
                  
                  // Get tools for this specific gateway
                  const gatewayToolCount = mcpService.isConnected(mcpUrl) 
                    ? mcpService.connectedGateways.get(mcpUrl)?.tools?.length || 0
                    : 0;
                  
                  return (
                    <div 
                      key={gateway.gatewayArn} 
                      className={`mcp-server-card ${isSelected ? 'active' : ''} ${isConnecting ? 'connecting' : ''}`}
                      onClick={handleToggle}
                    >
                      <div className="mcp-server-header">
                        <div className="mcp-server-status">
                          {isConnecting ? '⏳' : isSelected ? '✅' : '○'}
                        </div>
                        <div className="mcp-server-name">{gateway.name}</div>
                      </div>
                      {gateway.description && (
                        <div className="mcp-server-description">{gateway.description}</div>
                      )}
                      {isSelected && !isConnecting && gatewayToolCount > 0 && (
                        <div className="mcp-server-tools">
                          🔧 {gatewayToolCount} tool(s) from this server
                        </div>
                      )}
                      {isSelected && !isConnecting && gatewayToolCount === 0 && (
                        <div className="mcp-server-tools-empty">⚠️ No tools available</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {selectedGateways.length === 0 && !loadingGateways && availableGateways.length > 0 && (
              <small>Click on servers to enable tool use in conversations (multi-select supported)</small>
            )}
            {selectedGateways.length > 0 && (
              <small>✅ {selectedGateways.length} server(s) connected • {mcpTools.length} total tool(s)</small>
            )}
          </div>
          <div className="config-section">
            <label>System Prompt (with caching):</label>
            <textarea
              value={systemPrompt}
              onChange={(e) => saveSystemPrompt(e.target.value)}
              placeholder="Enter a system prompt to guide the AI's behavior... (cached for cost efficiency)"
              className="config-input system-prompt-textarea"
              rows="4"
            />
            <small>System prompt is cached to reduce costs. Click "📝 Prompts" to save and manage prompts.</small>
          </div>
          <div className="config-section">
            <label>
              <input
                type="checkbox"
                checked={useStreaming}
                onChange={(e) => setUseStreaming(e.target.checked)}
              />
              Use Streaming Responses
            </label>
          </div>
          <div className="config-section">
            <label>Your Role ARN (from Settings):</label>
            <input
              type="text"
              value={userRoleArn}
              readOnly
              placeholder="Loaded from Settings"
              className="config-input"
            />
            <small>This is your role that you configured in Settings</small>
          </div>
          {userRoleArn && (
            <div className="config-info">
              <strong>Role Assumption Flow:</strong>
              <ol>
                <li>Cognito Identity credentials</li>
                <li>→ Your role: {userRoleArn.split('/').pop()}</li>
              </ol>
              <small>Your role is automatically loaded from Settings configuration</small>
            </div>
          )}
        </div>
      )}

      {error && <div className="chat-error">{error}</div>}

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-welcome">
            <p>👋 Welcome to Larry Chat!</p>
            <p>Start a conversation with Larry by typing a message below.</p>
            {!userRoleArn && (
              <p className="config-hint" style={{color: 'orange'}}>
                ⚠️ Please configure your IAM role ARN in Settings (👤 button) to use Larry.
              </p>
            )}
          </div>
        ) : (
          messages.map((msg, index) => (
            <div key={index} className={`chat-message ${msg.role}`}>
              <div className="message-header">
                <span className="message-role">
                  {msg.role === 'user' ? '👤 You' : '🤖 Larry'}
                </span>
                <span className="message-timestamp">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </span>
              </div>
              <div className="message-content">
                {msg.contentBlocks ? (
                  // New block-based rendering for chronological order
                  msg.contentBlocks.map((block, blockIndex) => (
                    block.type === 'tool' ? (
                      <ToolUseNode key={`tool-${blockIndex}`} toolUse={block.toolUse} />
                    ) : (
                      <ReactMarkdown key={`text-${blockIndex}`} remarkPlugins={[remarkGfm]}>
                        {block.content}
                      </ReactMarkdown>
                    )
                  ))
                ) : (
                  // Legacy rendering for backward compatibility
                  <>
                    {msg.toolUses && msg.toolUses.length > 0 && (
                      <div className="tool-uses-container">
                        {msg.toolUses.map((toolUse, toolIndex) => (
                          <ToolUseNode key={toolUse.id || toolIndex} toolUse={toolUse} />
                        ))}
                      </div>
                    )}
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content}
                    </ReactMarkdown>
                  </>
                )}
              </div>
            </div>
          ))
        )}
        {loading && messages.length > 0 && messages[messages.length - 1].role !== 'assistant' && (
          <div className="chat-message assistant">
            <div className="message-header">
              <span className="message-role">🤖 Larry</span>
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Usage Stats Footer */}
      {(usageStats.totalTokens > 0 || usageStats.mode === 'tools') && (
        <div className="usage-stats-footer">
          <div className="usage-stat">
            <span className="stat-label">Mode:</span>
            <span className="stat-value">{usageStats.mode === 'tools' ? '🔧 Tools' : '💬 Chat'}</span>
          </div>
          {usageStats.totalTokens > 0 && (
            <>
              <div className="usage-stat">
                <span className="stat-label">Input:</span>
                <span className="stat-value">{usageStats.inputTokens.toLocaleString()} tokens</span>
              </div>
              <div className="usage-stat">
                <span className="stat-label">Output:</span>
                <span className="stat-value">{usageStats.outputTokens.toLocaleString()} tokens</span>
              </div>
              <div className="usage-stat">
                <span className="stat-label">Total:</span>
                <span className="stat-value stat-total">{usageStats.totalTokens.toLocaleString()} tokens</span>
              </div>
              {usageStats.cacheReadTokens > 0 && (
                <div className="usage-stat">
                  <span className="stat-label">Cache Read:</span>
                  <span className="stat-value stat-cache">{usageStats.cacheReadTokens.toLocaleString()} tokens</span>
                </div>
              )}
              {usageStats.cacheWriteTokens > 0 && (
                <div className="usage-stat">
                  <span className="stat-label">Cache Write:</span>
                  <span className="stat-value stat-cache">{usageStats.cacheWriteTokens.toLocaleString()} tokens</span>
                </div>
              )}
            </>
          )}
          {usageStats.stopReason && (
            <div className="usage-stat">
              <span className="stat-label">Stop Reason:</span>
              <span className="stat-value">{usageStats.stopReason}</span>
            </div>
          )}
          {usageStats.toolCallsCount > 0 && (
            <div className="usage-stat">
              <span className="stat-label">Tool Calls:</span>
              <span className="stat-value">{usageStats.toolCallsCount}</span>
            </div>
          )}
        </div>
      )}

      <div className="chat-input-container">
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message... (Press Enter to send, Shift+Enter for new line)"
          className="chat-input"
          rows="3"
          disabled={loading}
        />
        {loading ? (
          <button
            onClick={handleStopExecution}
            className="stop-button"
          >
            ⏹️ Stop
          </button>
        ) : (
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim()}
            className="send-button"
          >
            📤 Send
          </button>
        )}
      </div>

      {/* Prompts Panel Popup */}
      {showPromptsPanel && (
        <div className="tools-panel-overlay" onClick={() => setShowPromptsPanel(false)}>
          <div className="tools-panel" onClick={(e) => e.stopPropagation()}>
            <div className="tools-panel-header">
              <h3>📝 Prompt Catalog</h3>
              <button className="close-panel-button" onClick={() => setShowPromptsPanel(false)}>
                ×
              </button>
            </div>
            
            <div className="tools-panel-content">
              {/* Add New Prompt Section */}
              <div className="prompt-save-section" style={{ marginBottom: '24px', padding: '16px', borderRadius: '8px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '12px' }}>➕ Create New Prompt</h4>
                <input
                  type="text"
                  value={newPromptName}
                  onChange={(e) => setNewPromptName(e.target.value)}
                  placeholder="Prompt name..."
                  className="config-input"
                  style={{ marginBottom: '8px', width: '100%' }}
                />
                <textarea
                  value={newPromptText}
                  onChange={(e) => setNewPromptText(e.target.value)}
                  placeholder="Prompt text..."
                  className="config-input"
                  rows="4"
                  style={{ marginBottom: '8px', width: '100%', resize: 'vertical' }}
                />
                <button
                  onClick={handleSavePrompt}
                  disabled={savingPrompt || !newPromptName.trim() || !newPromptText.trim()}
                  className="save-prompt-button"
                  style={{ width: '100%' }}
                >
                  {savingPrompt ? '💾 Saving...' : '💾 Create Prompt'}
                </button>
              </div>

              {/* Saved Prompts Catalog */}
              <div className="tools-panel-list">
                <h4>📚 Your Prompts ({savedPrompts.length})</h4>
                {savedPrompts.length === 0 ? (
                  <div className="no-prompts" style={{ textAlign: 'center', padding: '32px', color: '#666' }}>
                    <p>📭 No prompts saved yet</p>
                    <p style={{ fontSize: '0.9em' }}>Create your first prompt using the form above.</p>
                  </div>
                ) : (
                  savedPrompts.map((prompt) => (
                    <div
                      key={prompt.promptName}
                      className="tool-panel-card prompt-card"
                      style={{ marginBottom: '12px' }}
                    >
                      {editingPrompt === prompt.promptName ? (
                        // Edit Mode
                        <div style={{ padding: '8px' }}>
                          <input
                            type="text"
                            value={editPromptName}
                            onChange={(e) => setEditPromptName(e.target.value)}
                            placeholder="Prompt name..."
                            className="config-input"
                            style={{ marginBottom: '8px', width: '100%' }}
                          />
                          <textarea
                            value={editPromptText}
                            onChange={(e) => setEditPromptText(e.target.value)}
                            placeholder="Prompt text..."
                            className="config-input"
                            rows="6"
                            style={{ marginBottom: '8px', width: '100%', resize: 'vertical' }}
                          />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={handleSaveEdit}
                              disabled={savingPrompt || !editPromptText.trim()}
                              className="save-prompt-button"
                              style={{ flex: 1 }}
                            >
                              {savingPrompt ? '💾 Saving...' : '💾 Save Changes'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={savingPrompt}
                              className="delete-prompt-button"
                              style={{ flex: 1 }}
                            >
                              ❌ Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <>
                          <div className="prompt-card-header">
                            <div className="tool-panel-name" style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                              {prompt.promptName}
                            </div>
                            <div className="prompt-card-actions" style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => handleLoadPrompt(prompt)}
                                className="load-prompt-button"
                                title="Load this prompt into chat"
                                style={{ fontSize: '0.9em', padding: '4px 8px' }}
                              >
                                📥 Load
                              </button>
                              <button
                                onClick={() => handleStartEdit(prompt)}
                                className="save-prompt-button"
                                title="Edit this prompt"
                                style={{ fontSize: '0.9em', padding: '4px 8px' }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleDeletePrompt(prompt.promptName)}
                                className="delete-prompt-button"
                                title="Delete this prompt"
                                style={{ fontSize: '0.9em', padding: '4px 8px' }}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                          <div className="tool-panel-description" style={{ 
                            marginTop: '8px', 
                            padding: '12px', 
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '150px',
                            overflow: 'auto',
                            fontSize: '0.9em',
                            lineHeight: '1.5'
                          }}>
                            {prompt.promptText}
                          </div>
                          {prompt.updatedAt && (
                            <small className="prompt-updated" style={{ display: 'block', marginTop: '8px', color: '#999' }}>
                              Last updated: {new Date(prompt.updatedAt).toLocaleString()}
                            </small>
                          )}
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tool Approval Dialog */}
      {pendingToolApproval && (
        <div className="tool-approval-dialog-overlay">
          <div className="tool-approval-dialog">
            <div className="tool-approval-dialog-header">
              <h3>⚠️ Tool Approval Required</h3>
            </div>
            <div className="tool-approval-dialog-content">
              <p>The AI wants to execute the following tool:</p>
              <div className="tool-approval-info-box">
                <div className="tool-name">🔧 {pendingToolApproval.toolName}</div>
                <div className="tool-input">
                  <strong>Input:</strong>
                  <pre>{JSON.stringify(pendingToolApproval.toolInput, null, 2)}</pre>
                </div>
              </div>
              <p className="approval-question">Do you want to allow this tool to execute?</p>
            </div>
            <div className="tool-approval-dialog-actions">
              <button 
                onClick={() => handleApprovalResponse(true, false)}
                className="approval-button approve"
              >
                ✅ Approve Once
              </button>
              <button 
                onClick={() => handleApprovalResponse(true, true)}
                className="approval-button approve-always"
              >
                ✅ Always Approve
              </button>
              <button 
                onClick={() => handleApprovalResponse(false, false)}
                className="approval-button deny"
              >
                ❌ Deny Once
              </button>
              <button 
                onClick={() => handleApprovalResponse(false, true)}
                className="approval-button deny-always"
              >
                🚫 Always Deny
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tool Approvals Panel */}
      {showToolApprovals && (
        <ToolApprovals 
          onClose={() => setShowToolApprovals(false)} 
          availableTools={mcpTools}
        />
      )}

      {/* Tools Panel Popup */}
      {showToolsPanel && (
        <div className="tools-panel-overlay" onClick={() => setShowToolsPanel(false)}>
          <div className="tools-panel" onClick={(e) => e.stopPropagation()}>
            <div className="tools-panel-header">
              <h3>🔧 MCP Tools</h3>
              <button className="close-panel-button" onClick={() => setShowToolsPanel(false)}>
                ×
              </button>
            </div>
            
            <div className="tools-panel-content">
              <div className="tools-panel-list">
                <h4>Available Tools ({mcpTools.length})</h4>
                {mcpTools.map((tool) => (
                  <div
                    key={tool.name}
                    className={`tool-panel-card ${selectedToolForTest?.name === tool.name ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedToolForTest(tool);
                      setToolTestParams({});
                      setToolTestResult(null);
                    }}
                  >
                    <div className="tool-panel-name">{tool.name}</div>
                    <div className="tool-panel-description">{tool.description}</div>
                  </div>
                ))}
              </div>

              {selectedToolForTest && (
                <div className="tool-panel-test">
                  <h4>Test: {selectedToolForTest.name}</h4>
                  <p className="tool-test-description">{selectedToolForTest.description}</p>

                  {selectedToolForTest.inputSchema?.properties && (
                    <div className="tool-test-params">
                      <h5>Parameters</h5>
                      {Object.entries(selectedToolForTest.inputSchema.properties).map(([paramName, paramDef]) => (
                        <div key={paramName} className="tool-test-param-group">
                          <label>
                            {paramName}
                            {selectedToolForTest.inputSchema.required?.includes(paramName) && (
                              <span className="required">*</span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={toolTestParams[paramName] || ''}
                            onChange={(e) => handleToolTestParamChange(paramName, e.target.value)}
                            placeholder={paramDef.description}
                          />
                          <small>{paramDef.description}</small>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={handleTestTool}
                    disabled={testingTool}
                    className="test-tool-button"
                  >
                    {testingTool ? '⏳ Testing...' : '▶ Execute Tool'}
                  </button>

                  {toolTestResult && (
                    <div className="tool-test-result">
                      <h5>Result</h5>
                      <pre>{JSON.stringify(toolTestResult, null, 2)}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BedrockChat;
